import * as ko from 'knockout';
import { Modal } from 'Core/Common/Modal';

import MollieButtonEditorTemplate from 'Core/Controls/TemplateDesigner/MollieButtonEditor/Templates/MollieButtonEditor.html';

const TEMPLATE_NAME = 'Core/Controls/TemplateDesigner/MollieButtonEditor/Templates/MollieButtonEditor';
ko.templates[TEMPLATE_NAME] = MollieButtonEditorTemplate;

const MOLLIE_REGEX = /\{MOLLIE\}(.*?)\{\/MOLLIE\}/;

export class MollieButtonEditor {
	private _modal: Modal;
	private _targetElement: HTMLElement;
	private _editor: any;

	_buttonText: KnockoutObservable<string>;
	_backgroundColor: KnockoutObservable<string>;
	_textColor: KnockoutObservable<string>;
	_fontSize: KnockoutObservable<string>;
	_paddingVertical: KnockoutObservable<string>;
	_paddingHorizontal: KnockoutObservable<string>;
	_borderRadius: KnockoutObservable<string>;
	_previewStyle: KnockoutComputed<object>;

	_tableName: KnockoutObservable<string>;
	_recordId: KnockoutObservable<string>;
	_currency: KnockoutObservable<string>;
	_amount: KnockoutObservable<string>;
	_description: KnockoutObservable<string>;

	constructor(element: HTMLElement, editor: any) {
		this._targetElement = element;
		this._editor = editor;

		const style = element.style;
		const mollieParams = this.parseMollieHref(element.getAttribute('href') || '');

		this._buttonText = ko.observable(element.innerText || 'Pay Now');
		this._backgroundColor = ko.observable(this.rgbToHex(style.backgroundColor) || '#007bff');
		this._textColor = ko.observable(this.rgbToHex(style.color) || '#ffffff');
		this._fontSize = ko.observable(style.fontSize || '14px');
		this._paddingVertical = ko.observable(this.parsePadding(style.padding, 'vertical'));
		this._paddingHorizontal = ko.observable(this.parsePadding(style.padding, 'horizontal'));
		this._borderRadius = ko.observable(parseInt(style.borderRadius) + '' || '4');

		this._tableName = ko.observable(mollieParams[0]);
		this._recordId = ko.observable(mollieParams[1]);
		this._currency = ko.observable(mollieParams[2]);
		this._amount = ko.observable(mollieParams[3]);
		this._description = ko.observable(mollieParams[4]);

		this._previewStyle = ko.computed(() => {
			return {
				display: 'inline-block',
				padding: `${this._paddingVertical()}px ${this._paddingHorizontal()}px`,
				backgroundColor: this._backgroundColor(),
				color: this._textColor(),
				fontSize: this._fontSize(),
				borderRadius: `${this._borderRadius()}px`,
				fontFamily: 'Arial, sans-serif',
				textDecoration: 'none'
			};
		});

		this._modal = new Modal({
			addClass: 'mollie-button-editor jBox-no-padding',
			width: 420,
			closeOnEsc: true,
			closeButton: true
		}, false);

		ko.applyBindings(this, this._modal.Wrapper);
	}

	GetTemplateName() {
		return TEMPLATE_NAME;
	}

	private AfterRender(): void {
		this._modal.Show();
	}

	OnSave(): void {
		const el = this._targetElement;
		const dom = this._editor?.dom;

		el.innerText = this._buttonText();

		if (dom) {
			dom.setAttrib(el, 'data-mollie-btn', 'true');
			dom.setAttrib(el, 'href',
				'http://{MOLLIE}' + this._tableName() + ';' + this._recordId() + ';' + this._currency() + ';' + this._amount() + ';' + this._description() + '{/MOLLIE}');
			dom.setStyles(el, {
				display: 'inline-block',
				padding: this._paddingVertical() + 'px ' + this._paddingHorizontal() + 'px',
				'background-color': this._backgroundColor(),
				color: this._textColor(),
				'font-size': this._fontSize(),
				'border-radius': this._borderRadius() + 'px',
				'font-family': 'Arial, sans-serif',
				'text-decoration': 'none'
			});
			this._editor.fire('change');
		}

		this._modal.Close();
		this.dispose();
	}

	OnCancel(): void {
		this._modal.Close();
		this.dispose();
	}

	private dispose(): void {
		if (this._modal.Wrapper) {
			ko.cleanNode(this._modal.Wrapper);
		}
		this._previewStyle.dispose();
	}

	private parseMollieHref(href: string): string[] {
		const match = MOLLIE_REGEX.exec(href);
		if (!match) return ['TableName', 'RecordId', 'EUR', 'Amount', 'Description'];
		const parts = match[1].split(';');
		return [
			parts[0] || 'TableName',
			parts[1] || 'RecordId',
			parts[2] || 'EUR',
			parts[3] || 'Amount',
			parts[4] || 'Description'
		];
	}

	private rgbToHex(color: string): string {
		if (!color) return '';
		if (color.startsWith('#')) return color;

		const match = color.match(/\d+/g);
		if (!match || match.length < 3) return color;

		const r = parseInt(match[0]);
		const g = parseInt(match[1]);
		const b = parseInt(match[2]);

		return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');
	}

	private parsePadding(padding: string, direction: 'vertical' | 'horizontal'): string {
		if (!padding) return direction === 'vertical' ? '10' : '20';

		const parts = padding.replace(/px/g, '').trim().split(/\s+/);
		if (parts.length === 1) return parts[0];
		if (parts.length === 2) return direction === 'vertical' ? parts[0] : parts[1];
		if (parts.length === 4) return direction === 'vertical' ? parts[0] : parts[1];

		return direction === 'vertical' ? '10' : '20';
	}
}
